import { PAGINATION } from '@/constants/pagination.constant';
import {
  NOTIFICATION_STATUS,
  RECEIVER_TYPE
} from '@/constants/notification.constant';
import { NotificationPermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getListNotification,
  watchLoadingNotification,
  watchNotificationList,
  watchPaginationNotification
} from '@/redux/slices/notification.slice';
import { formatDateToHHmmDDMMYYYY } from '@/utils/common';
import { PageURLs } from '@/utils/navigate';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Col,
  Row,
  Table,
  TableColumnsType,
  Tooltip,
  Typography
} from 'antd';
import { useCallback } from 'react';

interface NotificationManagementTableProps {
  type: 'EMAIL' | 'WEBAPP';
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  filter: FilterNotification;
}

export const NotificationManagementTable = ({
  type,
  onEdit,
  onDelete,
  filter
}: NotificationManagementTableProps) => {
  const { push } = useQuery();
  const dispatch = useAppDispatch();
  const loadingNotification = useAppSelector(watchLoadingNotification);
  const notificationList = useAppSelector(watchNotificationList);
  const pagination = useAppSelector(watchPaginationNotification);
  //   const { creatable, updatable, deletable } = usePermission();

  const handlePageChange = useCallback(
    ({
      page = PAGINATION.curPage,
      pageSize = PAGINATION.limitPage
    }: {
      page?: number;
      pageSize?: number;
    }) => {
      dispatch(
        getListNotification({
          currentPage: page,
          limitPage: pageSize,
          filter: { ...filter }
        })
      );
    },
    [dispatch, filter]
  );

  const handleEdit = (id: number) => {
    onEdit?.(id);
  };

  const handleDelete = (id: number) => {
    onDelete?.(id);
  };

  const handleCreateNotification = () => {
    // if (!creatable) {
    //   return;
    // }

    push(
      `${PageURLs.ofNotificationManagement()}?mode=${type === 'EMAIL' ? NotificationPermissionMode.CREATEEMAIL : NotificationPermissionMode.CREATEWEBAPP}`
    );
  };

  const columns: TableColumnsType<NotificationItem> = [
    {
      title: 'Stt',
      render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
      width: 60
    },
    {
      title: `Tiêu đề${type === 'EMAIL' ? ' email' : ''}`,
      dataIndex: 'TITLE',
      key: 'TITLE',
      ellipsis: true
    },
    {
      title: 'Trạng thái gửi',
      dataIndex: 'STATUS',
      key: 'STATUS',
      render: (status: 'PENDING' | 'SEND') => {
        return NOTIFICATION_STATUS[status] || status;
      }
    },
    {
      title: 'Người nhận',
      dataIndex: 'TARGET_TYPE',
      key: 'TARGET_TYPE',
      render: (value) =>
        value === 'ALL' ? RECEIVER_TYPE.ALL : RECEIVER_TYPE.ADJUST
    },
    {
      title: 'Trạng thái sức khỏe',
      dataIndex: 'TARGET_GROUP',
      key: 'TARGET_GROUP',
      width: 200,
      render: (_, record) => {
        const item = record as NotificationItem;
        if (item.TARGET_TYPE === 'ALL') {
          return '';
        }
        if (item.TARGET_GROUP && item.TARGET_GROUP.length > 0) {
          return item.TARGET_GROUP.map((group) => group.DISPLAY_NAME).join(
            ', '
          );
        }
        return '';
      }
    },
    {
      title: 'Cập nhật mới nhất',
      align: 'center',
      width: 150,
      render: (_, record) => (
        <div css={updateCreatedByStyle}>
          <span>
            {formatDateToHHmmDDMMYYYY((record as NotificationItem).UPDATED_AT)}
          </span>
          <span>{(record as NotificationItem).UPDATED_NAME}</span>
        </div>
      )
    },
    {
      title: 'Ngày tạo',
      key: 'CREATED_AT',
      align: 'center',
      render: (_, record) => (
        <div css={updateCreatedByStyle}>
          <span>{formatDateToHHmmDDMMYYYY((record as any).CREATED_AT)}</span>
          <span>{(record as NotificationItem).CREATED_NAME}</span>
        </div>
      )
    },
    {
      title: 'Thao tác',
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      width: 104,
      align: 'center',
      render: (_, record) => (
        <Row gutter={[8, 10]} justify='center'>
          <Col>
            <Tooltip
              title={`Cập nhật ${type === 'EMAIL' ? 'Email' : 'Web/App'}`}
            >
              <Button
                // disabled={!updatable}
                onClick={() => handleEdit((record as any).ID)}
                type='primary'
                icon={<EditOutlined />}
                size='small'
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip
              title={`Xoá thông báo ${type === 'EMAIL' ? 'Email' : 'Web/App'}`}
            >
              <Button
                // disabled={!deletable}
                onClick={() => handleDelete((record as any).ID)}
                type='primary'
                danger
                icon={<DeleteOutlined />}
                size='small'
              />
            </Tooltip>
          </Col>
        </Row>
      )
    }
  ];

  return (
    <div css={containerStyle}>
      <Row css={headerStyle} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Danh sách thông báo {type === 'EMAIL' ? 'email' : 'Web/App'}
          </Typography.Title>
        </Col>

        <Button
          icon={<PlusOutlined />}
          iconPosition='start'
          //   disabled={!creatable}
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#1890FF',
            color: '#1890FF',
            padding: '0 0.8rem'
          }}
          onClick={handleCreateNotification}
        >
          Tạo thông báo {type === 'EMAIL' ? 'email' : ''}
        </Button>
      </Row>

      <div css={tableStyle}>
        <Table<NotificationItem>
          columns={columns}
          dataSource={notificationList}
          loading={loadingNotification}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: true,
            current: pagination.curPage,
            pageSize: pagination.limitPage,
            total: pagination.totalRows,
            onChange: (page, pageSize) => {
              handlePageChange({ page, pageSize });
            }
          }}
          scroll={{ x: 1200 }}
          sticky={{
            offsetHeader: 60
          }}
          rowKey='ID'
        />
      </div>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const headerStyle = css`
  width: 100%;
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 0.8rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const tableStyle = css`
  .ant-spin-container {
    padding: 4px !important;
  }

  background: var(--white-color);
  .ant-table-row .ant-table-cell {
    padding: 4px 8px !important;
  }
  .ant-table-thead .ant-table-cell {
    background-color: var(--blue-dark-color);
    color: var(--white-color);
  }
  .ant-table-cell::before {
    display: none !important;
  }

  .ant-table-wrapper .ant-table-cell {
    padding: 8px 8px !important;
  }

  .btActionStyle {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:first-child {
    border-start-start-radius: 0px !important;
  }

  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:last-child {
    border-start-end-radius: 0px !important;
  }
`;

const updateCreatedByStyle = css`
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 1.4rem;
  text-align: left;
`;
